import { Modal as PopupModal } from '@lmc/ui-kit'
import * as Sentry from '@sentry/react'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import { homeChangedBlockingModalConstants as keys } from 'locale/homeChangedBlockingModal'
import { Trans, useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import CONSTANTS from 'utils/constants'
import { broadcastHomeSwitch } from '../../common/services/homeSwitchBroadcast'
import { resetHomeChangedModalGuard } from '../../common/services/sso'
import { showToast } from '../../common/utils'
import { history } from '../../store'
import Modal from './index'

type HomeChangedModalProps = {
  isOpen: boolean
  close: () => void
  currentHomeName?: string
  requestedHomeId?: string
  userId?: string
  ownHomeName?: string
}

const HomeChangedBlockingModal = ({
  isOpen,
  close,
  currentHomeName,
  requestedHomeId,
  userId,
  ownHomeName
}: HomeChangedModalProps) => {
  const { t } = useTranslation(NAMESPACE.HOME_CHANGED_BLOCKING_MODAL)

  if (!isOpen) return null

  const homeName = currentHomeName || 'another home'

  const handleContinue = () => {
    const unlisten = history.listen(() => {
      unlisten()
      resetHomeChangedModalGuard()
      close()
      window.location.reload()
    })
    history.push('/')
  }

  const handleSwitchBack = async () => {
    if (requestedHomeId && userId) {
      try {
        const baseUrl = CONSTANTS.VITE_APP_CM_API
        const response = await fetch(`${baseUrl}/list/carers/${userId}`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ home: requestedHomeId })
        })
        if (!response.ok) {
          throw new Error(`Switch home failed: ${response.status}`)
        }
      } catch (error) {
        Sentry.captureException(error)
        showToast({
          type: 'error',
          title: t(generalConstants.ERROR, { ns: NAMESPACE.GENERAL })
        })
        return
      }
    }
    if (requestedHomeId) {
      broadcastHomeSwitch({
        newHomeId: requestedHomeId,
        newHomeName: ownHomeName || ''
      })
    }
    resetHomeChangedModalGuard()
    close()
  }

  return (
    <PopupModal
      localisation={{
        title: t(keys.HOME_CHANGED_TITLE),
        confirmText: t(keys.HOME_CHANGED_CONTINUE, { homeName }),
        cancelText: requestedHomeId
          ? t(keys.HOME_CHANGED_SWITCH_BACK)
          : undefined
      }}
      isModalVisible={isOpen}
      onConfirm={handleContinue}
      onCancel={requestedHomeId ? handleSwitchBack : handleContinue}
      closeButton={false}
      testId="home-changed"
    >
      <div style={{ padding: '12px 0' }}>
        <Trans
          t={t}
          i18nKey={keys.HOME_CHANGED_BODY}
          values={{ homeName }}
          components={{ strong: <strong /> }}
        />
      </div>
    </PopupModal>
  )
}

const MODAL_ID = 'homeChangedModal'

const mapStateToProps = (state: any) => ({
  userId: state.global.user?.id,
  ownHomeName: state.global.home?.name
})

const wrappedModal = connect(mapStateToProps)(HomeChangedBlockingModal)

export default Modal(wrappedModal, MODAL_ID)
